import {Injectable} from "@angular/core";
import {GridLayoutService} from "@irb/irb-common/interfaces/grid-layout.service";
import {DeleteHeaderComponent} from "@irb/irb-common/components/grid/renderers/delete.header.component";
import {DeleteCellComponent} from "@irb/irb-common/components/grid/renderers/delete.cell.component";
import {NumberTextBoxRendererComponent} from "@irb-common/components/grid/renderers/number-text-box.cell.component";
import {UtilityService} from "@irb/irb-common/services/utility.service";
import {BuyToCoverSettingsComponent} from "./buy-to-cover-settings.component";
import {OptionsFormatterService} from "../../options-formatter.service";
import {OptionToCloseCellRenderer} from "./option-to-close-cell/option-to-close-cell-renderer.component";

@Injectable({
    providedIn: 'root'
})
export class BuyToCoverGridLayoutService implements GridLayoutService<BuyToCoverSettingsComponent> {

    sizeFor: any;

    constructor(private util: UtilityService,
        public optionFormatter: OptionsFormatterService) {
        this.sizeFor = {
            "underlyingSymbol": 150,
            "optionSymbol": 190,
            "daysToExpiration": 190,
            "percentOfOptionToBuyBack": 240
        };
    }

    getFrameworkComponents() {
        return {
            "DeleteHeaderComponent": DeleteHeaderComponent,
            "DeleteCellComponent": DeleteCellComponent,
            "OptionToCloseCellRenderer": OptionToCloseCellRenderer,
            "NumberTextBoxRendererComponent": NumberTextBoxRendererComponent
        };
    }

    isGridEditable(params: any) {
        return !params.context.componentParent.isReadOnly;
    }

    isExpirationDaysEditable(params: any) {
        return !params.context.componentParent.isReadOnly && params.data.optionSelectionMode !== 'OPTION_CHAIN';
    }

    // didn't find use of it
    // percentOfOptionToBuyBackValueGetter(params: any) {
    //     const val = params.context.componentParent.decimalPipe.transform(params.data.percentOfOptionToBuyBack, '1.2-2');
    //     return val + " %";
    // }

    daysToExpirationValue(params: any) {
        let dte: any;
        if (params.data.optionSelectionMode) {
            switch(params.data.optionSelectionMode) {
                case 'ALL_CALLS':
                    dte = Number(params.data.daysToExpiration);
                    break;
                case 'ALL_PUTS':
                    dte = Number(params.data.daysToExpiration);
                    break;
                case 'OPTION_CHAIN':
                    dte = '-';
                    break;
            }
        }
        return dte;
    }

    daysToExpirationValueSetter(params: any) {
        params.data.daysToExpiration = Number(params.newValue);
        return true;
    }

    optionToCloseValueSetter(params: any) {
        if (params.oldValue !== params.newValue) {
            switch(params.newValue) {
                case 'All Calls':
                    params.data.optionSelectionMode = "ALL_CALLS";
                    params.data.optionSymbol = null;
                    break;
                case 'All Puts':
                    params.data.optionSelectionMode = "ALL_PUTS";
                    params.data.optionSymbol = null;
                    break;
            }
        }
        return true;
    }

    getColumnDefs(parent: BuyToCoverSettingsComponent) {
        return [
            {
                headerName: 'Delete All',
                headerComponent: 'DeleteHeaderComponent',
                headerComponentParams: {
                    message: "Remove all items?",
                    proceed: parent.deleteAll.bind(parent),
                    cancel: parent.cancelDelete.bind(parent),
                    disableDelete: parent.isReadOnly
                },
                cellRenderer: 'DeleteCellComponent',
                cellRendererParams:  {
                    message: "Remove this item?",
                    proceed: parent.delete.bind(parent),
                    cancel: parent.cancelDelete.bind(parent),
                    disableDelete: (params: any) => this.isGridEditable(params)
                },
                resizable: false,
                minWidth: 25,
                maxWidth: 25,
                width: 25,
            },
            {
                headerName: 'Underlying Symbol',
                field: 'underlyingSymbol',
                minWidth: this.sizeFor.underlyingSymbol,
                flex: 1,
                resizable: false,
                sortable: false,
                editable: false,
                suppressMovable: true,
                cellHeight: 30,
            },
            {
                headerName: 'Option to close',
                headerComponentParams: {
                    irbColInfo: `<p>This is the existing option position iRebal will look to close. You can select a specific option or you can use the generic All puts or All calls selections to close or roll your options all together.</p>`
                },
                field: 'optionSymbol',
                minWidth: this.sizeFor.optionSymbol,
                flex: 1,
                valueSetter: this.optionToCloseValueSetter,
                cellClass: 'dropdownFilterCell',
                cellRenderer: 'OptionToCloseCellRenderer',
                cellRendererParams: (params: any) => {
                    return {
                        isDisabled: !this.isGridEditable(params),
                    };
                },
                sortable: false,
                resizable: false,
                suppressMovable: true,
                cellHeight: 30,
            },
            {
                headerName: '< Days to Expiration',
                headerComponentParams: {
                    irbColInfo: `<p>If all puts or all calls are selected in the Option to close column, then the Less than Days to expiration is a mandatory field. Options held that both meet the type of option and have fewer days left to expiration than the input will be closed.</p>`
                },
                field: 'daysToExpiration',
                // valueGetter: this.daysToExpirationValue,
                // valueSetter: this.daysToExpirationValueSetter,
                // cellEditorFramework: DaysToExpirationCellEditor,
                minWidth: this.sizeFor.daysToExpiration,
                flex: 1,
                // editable: this.isExpirationDaysEditable,
                cellClass: "numberTextBoxCell",
                cellRenderer: "NumberTextBoxRendererComponent",
                cellRendererParams: () => {
                    return {
                        min: 1,
                        max: 365,
                        autofocus: true,
                        invalidRangeMessage:"Please enter only numbers between 1 and 365",
                        isPercentage: false,
                        onChange: (params: any, c: any) => {
                            if (params && params.data.daysToExpiration != c.value && c.isValid) {
                                params.data.daysToExpiration = parseFloat(c.value);
                                params.api.redrawRows({ rowNodes: [params.node] });
                            }
                        },
                    };
                },
                resizable: false,
                suppressMovable: true,
                sortable: false
            },
            {
                headerName: 'Percent of option to close',
                headerComponentParams: {
                    irbColInfo: `<p>Enter a % of the option security to buy to cover. Examples: entering 100% tells iRebal to buy to cover the entire open option position; entering 50% will buy to cover 50% of the option share quantity in the account.</p>`
                },
                field: 'percentOfOptionToBuyBack',
                // valueGetter: this.percentOfOptionToBuyBackValueGetter,
                // cellEditorFramework: OptionToBuyBackCellEditor,
                minWidth: this.sizeFor.percentOfOptionToBuyBack,
                flex: 1,
                // editable: (params: any) => this.isGridEditable(params),
                cellClass: "numberTextBoxCell",
                cellRenderer: "NumberTextBoxRendererComponent",
                cellRendererParams: () => {
                    return {
                        min: 0,
                        max: 100,
                        places: 2,
                        isPercentage: true,
                        onChange: (params: any, c: any) => {
                            if (params && params.data.percentOfOptionToBuyBack != c.value && c.isValid) {
                                params.data.percentOfOptionToBuyBack = parseFloat(c.value);
                                params.api.redrawRows({ rowNodes: [params.node] });
                            }
                        },
                    };
                },
                resizable: false,
                suppressMovable: true,
                sortable: false
            }
        ];
    }
}
